import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _Trash;
import { useEffect, useRef } from 'react';
import Trash2 from '@crehana/compass.icons.outline/Trash2';
import { useTranslation } from '@crehana/i18n';
import { useDebounce } from '@crehana/react-hooks';
import { notEmpty } from '@crehana/utils';
import { InputField } from "../../InputField";
import { Button } from "../Button";
import { ChipGroup } from "../ChipGroup";
import { ConditionalSelect, DefaultConditionalTypesEnum, defaultOptions } from "../conditional-select";
import { DatePickerWithTabs } from "../DatePicker/date-picker-with-tabs";
import { AsyncCheckboxesDropdown, Dropdown, DropdownContent, DropdownValue } from "../Dropdown";
import { AsyncLeveledDropdown } from "../Dropdown/pre-built-variants/async/async-leveled-dropdown";
import { useDefaultValues } from "./hooks/use-default-values";
import { useSegmentationState } from "./hooks/use-segmentation-state";
import locales from "./i18n";
import { TCustomFieldTypeEnum } from "./types";
import { mapStateToSegmentationCondition } from "./utils";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var LabelWrapper = function LabelWrapper(_ref) {
  var label = _ref.label,
    children = _ref.children;
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-flex tw-flex-col tw-w-full",
    children: [/*#__PURE__*/_jsx("label", {
      className: "tw-font-subtitle5 tw-mb-0.8 tw-block",
      children: label
    }), children]
  });
};
export var SegmentationFieldInput = function SegmentationFieldInput(_ref2) {
  var _state$availableField, _state$value3, _state$value4, _state$value5, _state$value5$date, _state$value6, _state$value6$date, _state$value7, _state$value7$date, _state$value10;
  var categories = _ref2.categories,
    _ref2$defaultCategory = _ref2.defaultCategory,
    defaultCategory = _ref2$defaultCategory === void 0 ? 'alphabetic' : _ref2$defaultCategory,
    defaultValue = _ref2.defaultValue,
    onCategorySelect = _ref2.onCategorySelect,
    onFieldSelect = _ref2.onFieldSelect,
    onChange = _ref2.onChange,
    onDelete = _ref2.onDelete;
  var _useTranslation = useTranslation(),
    languageCode = _useTranslation.languageCode;
  var t = locales[languageCode];
  var _useDebounce = useDebounce(),
    debounceValue = _useDebounce.debounceValue,
    handleChange = _useDebounce.handleChange;
  var _useDefaultValues = useDefaultValues(defaultValue),
    initialCategory = _useDefaultValues.defaultCategory,
    defaultFieldId = _useDefaultValues.defaultFieldId,
    defaultFieldLabel = _useDefaultValues.defaultFieldLabel,
    defaultSelectedValues = _useDefaultValues.defaultSelectedValues,
    defaultInputValue = _useDefaultValues.defaultInputValue,
    defaultDateValue = _useDefaultValues.defaultDateValue,
    defaultConditionalType = _useDefaultValues.defaultConditionalType,
    defaultFormType = _useDefaultValues.defaultFormType;
  var _useSegmentationState = useSegmentationState({
      category: initialCategory || defaultCategory,
      fieldId: defaultFieldId,
      availableFields: [],
      fieldValues: [],
      conditionalType: defaultConditionalType,
      isTagSelect: false,
      isLoadingValues: false,
      value: {
        selected: defaultSelectedValues,
        input: defaultInputValue,
        date: defaultDateValue
      }
    }),
    state = _useSegmentationState.state,
    updateState = _useSegmentationState.updateState,
    updateValue = _useSegmentationState.updateValue,
    handleClear = _useSegmentationState.handleClear;
  var fieldLabel = ((_state$availableField = state.availableFields.find(function (f) {
    return f.value == state.fieldId;
  })) === null || _state$availableField === void 0 ? void 0 : _state$availableField.label) || defaultFieldLabel || state.fieldId;
  var handleCategorySelect = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(category) {
      var availableFields;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            handleClear();
            _context.next = 3;
            return onCategorySelect === null || onCategorySelect === void 0 ? void 0 : onCategorySelect(category);
          case 3:
            availableFields = _context.sent;
            updateState({
              category: category,
              availableFields: availableFields || []
            });
            return _context.abrupt("return", (availableFields || []).map(function (f) {
              return _objectSpread(_objectSpread({}, f), {}, {
                type: 'text'
              });
            }));
          case 6:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function handleCategorySelect(_x) {
      return _ref3.apply(this, arguments);
    };
  }();

  // biome-ignore lint/suspicious/useAwait: async test without await
  var handleAttributeSelect = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(values) {
      var _Object$entries, _Object$entries2, _Object$entries2$, category, value, attributeId;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _Object$entries = Object.entries(values), _Object$entries2 = _slicedToArray(_Object$entries, 1), _Object$entries2$ = _slicedToArray(_Object$entries2[0], 2), category = _Object$entries2$[0], value = _Object$entries2$[1];
            attributeId = value[0];
            updateState({
              category: category,
              fieldId: attributeId
            });
            if (onFieldSelect && category === 'alphabetic') {
              handleFetchFieldValues(attributeId);
            }
          case 4:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function handleAttributeSelect(_x2) {
      return _ref4.apply(this, arguments);
    };
  }();
  var handleFetchFieldValues = /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee3(attributeId, searchTerm) {
      var result, values;
      return _regeneratorRuntime().wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            if (onFieldSelect) {
              _context3.next = 2;
              break;
            }
            return _context3.abrupt("return");
          case 2:
            updateState({
              isLoadingValues: true
            });
            _context3.prev = 3;
            result = onFieldSelect(attributeId, searchTerm);
            if (!(result instanceof Promise)) {
              _context3.next = 11;
              break;
            }
            _context3.next = 8;
            return result;
          case 8:
            _context3.t0 = _context3.sent;
            _context3.next = 12;
            break;
          case 11:
            _context3.t0 = result;
          case 12:
            values = _context3.t0;
            updateState({
              fieldValues: values,
              isLoadingValues: false
            });
            _context3.next = 19;
            break;
          case 16:
            _context3.prev = 16;
            _context3.t1 = _context3["catch"](3);
            updateState({
              fieldValues: [],
              isLoadingValues: false
            });
          case 19:
          case "end":
            return _context3.stop();
        }
      }, _callee3, null, [[3, 16]]);
    }));
    return function handleFetchFieldValues(_x3, _x4) {
      return _ref5.apply(this, arguments);
    };
  }();
  var handleConditionalSelect = function handleConditionalSelect(value, inputValues) {
    var selectedOption = defaultOptions.find(function (opt) {
      return opt.value === value;
    });
    if (!selectedOption) {
      return;
    }
    var isSelectType = state.category === 'alphabetic' && Boolean(selectedOption.select);
    var isNumericRange = state.category === 'numeric' && value === DefaultConditionalTypesEnum.RANGE;
    updateState({
      isTagSelect: isSelectType,
      conditionalType: selectedOption.value.toString()
    });
    if (inputValues !== null && inputValues !== void 0 && inputValues.length) {
      if (isNumericRange) {
        updateValue({
          input: inputValues
        });
      } else if (isSelectType) {
        updateValue({
          selected: inputValues,
          input: undefined
        });
      } else {
        updateValue({
          input: inputValues[0],
          selected: undefined
        });
      }
    }
  };
  var handleChipDelete = function handleChipDelete(value) {
    var _state$value, _state$value$selected;
    if (state.category !== 'alphabetic' || !state.isTagSelect) {
      return;
    }
    updateValue({
      selected: (_state$value = state.value) === null || _state$value === void 0 ? void 0 : (_state$value$selected = _state$value.selected) === null || _state$value$selected === void 0 ? void 0 : _state$value$selected.filter(function (v) {
        return v !== value;
      })
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    var _state$value2;
    if (debounceValue === undefined || debounceValue === '') {
      return;
    }
    if (Array.isArray((_state$value2 = state.value) === null || _state$value2 === void 0 ? void 0 : _state$value2.input) && state.value.input.length === 2) {
      return;
    }
    updateValue({
      input: debounceValue
    });
  }, [debounceValue]);
  var isInitialized = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    var _defaultValue$values, _defaultValue$values$, _defaultValue$values2, _defaultValue$values3, _defaultValue$values4;
    if (isInitialized.current) {
      return;
    }
    if (defaultFormType === TCustomFieldTypeEnum.Number && (defaultValue === null || defaultValue === void 0 ? void 0 : (_defaultValue$values = defaultValue.values) === null || _defaultValue$values === void 0 ? void 0 : (_defaultValue$values$ = _defaultValue$values[0]) === null || _defaultValue$values$ === void 0 ? void 0 : _defaultValue$values$.operator) === DefaultConditionalTypesEnum.RANGE && (defaultValue === null || defaultValue === void 0 ? void 0 : (_defaultValue$values2 = defaultValue.values) === null || _defaultValue$values2 === void 0 ? void 0 : (_defaultValue$values3 = _defaultValue$values2[0]) === null || _defaultValue$values3 === void 0 ? void 0 : (_defaultValue$values4 = _defaultValue$values3.value) === null || _defaultValue$values4 === void 0 ? void 0 : _defaultValue$values4.length) === 2) {
      updateValue({
        input: defaultValue.values[0].value
      });
      updateState({
        conditionalType: DefaultConditionalTypesEnum.RANGE
      });
    }
    isInitialized.current = true;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    var parsedData = mapStateToSegmentationCondition(state, fieldLabel);
    if (parsedData.isValid) {
      onChange === null || onChange === void 0 ? void 0 : onChange(parsedData.data);
    }
  }, [(_state$value3 = state.value) === null || _state$value3 === void 0 ? void 0 : _state$value3.input, (_state$value4 = state.value) === null || _state$value4 === void 0 ? void 0 : _state$value4.selected, (_state$value5 = state.value) === null || _state$value5 === void 0 ? void 0 : (_state$value5$date = _state$value5.date) === null || _state$value5$date === void 0 ? void 0 : _state$value5$date.tab, (_state$value6 = state.value) === null || _state$value6 === void 0 ? void 0 : (_state$value6$date = _state$value6.date) === null || _state$value6$date === void 0 ? void 0 : _state$value6$date.dates.from, (_state$value7 = state.value) === null || _state$value7 === void 0 ? void 0 : (_state$value7$date = _state$value7.date) === null || _state$value7$date === void 0 ? void 0 : _state$value7$date.dates.to, state.conditionalType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (defaultFormType && state.fieldId) {
      var isListType = defaultFormType === TCustomFieldTypeEnum.Multiple;
      updateState({
        isTagSelect: isListType
      });
      if (onFieldSelect && defaultFormType === TCustomFieldTypeEnum.Multiple) {
        handleAttributeSelect(_defineProperty({}, state.category, [state.fieldId]));
      }
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (debounceValue === undefined || !state.fieldId) {
      return;
    }
    if (onFieldSelect) {
      handleFetchFieldValues(state.fieldId, debounceValue);
    }
  }, [debounceValue, state.fieldId]);
  var renderInputByCategory = function renderInputByCategory() {
    var _state$value8, _state$value8$selecte, _state$value9, _state$value0, _state$value0$date, _state$value1, _state$value1$date;
    if (!state.category) {
      return null;
    }
    switch (state.category) {
      case 'alphabetic':
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(LabelWrapper, {
            label: t.CONDITIONAL,
            children: /*#__PURE__*/_jsx(ConditionalSelect, {
              placeholder: t.CONDITIONAL_PLACEHOLDER,
              onSelect: function onSelect(_ref6) {
                var option = _ref6.option,
                  values = _ref6.values;
                return handleConditionalSelect(option, values);
              },
              defaultValue: {
                option: state.conditionalType
              },
              disabled: !state.fieldId,
              showValueInDropdown: true,
              noInputs: true
            })
          }), state.isTagSelect ? /*#__PURE__*/_jsx(LabelWrapper, {
            label: t.VALUE,
            children: /*#__PURE__*/_jsx(AsyncCheckboxesDropdown, {
              debounceTime: 0,
              onSearch: function onSearch(searchTerm) {
                return handleChange({
                  target: {
                    value: searchTerm
                  }
                });
              },
              selected: (_state$value8 = state.value) === null || _state$value8 === void 0 ? void 0 : (_state$value8$selecte = _state$value8.selected) === null || _state$value8$selecte === void 0 ? void 0 : _state$value8$selecte.map(function (v) {
                return {
                  value: v,
                  label: v.toString()
                };
              }),
              options: state.fieldValues || [],
              loading: state.isLoadingValues,
              onSelect: function onSelect(value) {
                return updateValue({
                  selected: value === null || value === void 0 ? void 0 : value.map(function (v) {
                    return v.value;
                  })
                });
              },
              placeholder: t.VALUE_PLACEHOLDER,
              searchProps: {
                searchPlaceholder: t.SEARCH_PLACEHOLDER,
                noFoundPlaceholderTitle: t.NO_RESULTS
              }
            })
          }) : /*#__PURE__*/_jsx(InputField, {
            placeholder: t.VALUE_PLACEHOLDER,
            label: t.VALUE,
            containerClassName: "tw-flex tw-flex-col tw-w-full",
            onChange: handleChange,
            disabled: !state.fieldId && !state.conditionalType,
            defaultValue: (_state$value9 = state.value) === null || _state$value9 === void 0 ? void 0 : _state$value9.input
          })]
        });
      case 'date':
        return /*#__PURE__*/_jsx(LabelWrapper, {
          label: t.DATE,
          children: /*#__PURE__*/_jsx(DatePickerWithTabs, {
            className: "tw-flex tw-w-full tw-flex-1 tw-flex-grow-0",
            enabledTabs: ['single', 'range'],
            defaultTab: (_state$value0 = state.value) === null || _state$value0 === void 0 ? void 0 : (_state$value0$date = _state$value0.date) === null || _state$value0$date === void 0 ? void 0 : _state$value0$date.tab,
            value: (_state$value1 = state.value) === null || _state$value1 === void 0 ? void 0 : (_state$value1$date = _state$value1.date) === null || _state$value1$date === void 0 ? void 0 : _state$value1$date.dates,
            onChange: function onChange(value) {
              return updateValue({
                date: value
              });
            },
            disabled: !state.fieldId
          })
        });

      //TODO: Ensure that values inserted are numbers and not repeated
      case 'numeric':
        {
          var defaultInputValueArray = (Array.isArray(defaultInputValue) ? defaultInputValue : [defaultInputValue]).filter(notEmpty);
          return /*#__PURE__*/_jsx(LabelWrapper, {
            label: t.CONDITIONAL,
            children: /*#__PURE__*/_jsx(ConditionalSelect, {
              placeholder: t.CONDITIONAL_PLACEHOLDER,
              onSelect: function onSelect(_ref7) {
                var option = _ref7.option,
                  values = _ref7.values;
                return handleConditionalSelect(option, values);
              },
              defaultValue: {
                option: defaultConditionalType,
                values: defaultInputValueArray
              },
              showValueInDropdown: true,
              type: "number"
            })
          });
        }
      default:
        return null;
    }
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs("div", {
      className: "tw-flex tw-flex-col tw-w-full tw-bg-neutral-light-100 tw-p-2.4 tw-rounded-[1.2rem] tw-mb-[1.4rem]",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "tw-flex tw-justify-between tw-items-center tw-gap-1",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "tw-flex tw-flex-col tw-w-full tw-max-w-20",
          children: [/*#__PURE__*/_jsx("label", {
            className: "tw-font-subtitle5 tw-mb-0.8 tw-block",
            children: t.ATTRIBUTES
          }), /*#__PURE__*/_jsxs(Dropdown, {
            children: [/*#__PURE__*/_jsx(DropdownValue, {
              className: "tw-truncate",
              placeholder: t.ATTRIBUTE_PLACEHOLDER,
              forcedValue: fieldLabel
            }), /*#__PURE__*/_jsx(DropdownContent, {
              children: /*#__PURE__*/_jsx(AsyncLeveledDropdown, {
                categories: categories,
                onCategorySelect: handleCategorySelect,
                onSelectionChange: function onSelectionChange(values) {
                  return handleAttributeSelect(values);
                },
                trackByCategory: true,
                multiselect: false
              })
            })]
          })]
        }), renderInputByCategory(), /*#__PURE__*/_jsx(Button, {
          type: "button",
          variant: "icon",
          className: "tw-mt-2.4",
          onClick: onDelete,
          children: _Trash || (_Trash = /*#__PURE__*/_jsx(Trash2, {
            size: 24,
            className: "tw-text-neutral-light-500"
          }))
        })]
      }), state.isTagSelect && ((_state$value10 = state.value) === null || _state$value10 === void 0 ? void 0 : _state$value10.selected) && state.value.selected.length > 0 && /*#__PURE__*/_jsx(ChipGroup, {
        items: state.value.selected.map(function (v) {
          var _state$fieldValues$fi;
          return {
            value: v,
            label: ((_state$fieldValues$fi = state.fieldValues.find(function (av) {
              return av.value === v;
            })) === null || _state$fieldValues$fi === void 0 ? void 0 : _state$fieldValues$fi.label) || v.toString()
          };
        }),
        selected: state.value.selected.map(function (v) {
          return v.toString();
        }),
        onDelete: function onDelete(value) {
          return handleChipDelete(value);
        },
        onClearAll: function onClearAll() {
          updateValue({
            selected: []
          });
          onChange === null || onChange === void 0 ? void 0 : onChange(undefined);
        },
        maxChipsRows: 2,
        clearButtonLabel: t.CLEAR_ALL
      })]
    })
  });
};